import React, { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "../lib/api";
import StatusPill from "../components/StatusPill";
import StatusTimeline from "../components/StatusTimeline";
import OnHoldBanner from "../components/OnHoldBanner";
import { formatDate } from "../lib/constants";

/**
 * ClientProject — parent for the client's view of a single project.
 * Renders the header (status timeline / on-hold / summary). Sub-navigation
 * (Overview / Documents / Uploads / Profile) is handled by the left sidebar
 * in ClientLayout.
 */
export default function ClientProject() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [activity, setActivity] = useState([]);
  const [documents, setDocuments] = useState([]);
  const location = useLocation();

  const load = useCallback(async () => {
    const [{ data: p }, { data: act }, { data: docs }] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get(`/activity`, { params: { project_id: id } }),
      api.get(`/projects/${id}/documents`),
    ]);
    setProject(p);
    setActivity(act);
    setDocuments(docs);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!project) return <div className="max-w-6xl mx-auto px-6 py-10 label">Loading…</div>;

  return (
    <div className="fade-up">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-10">
        <Link to="/portal" className="inline-flex items-center gap-2 text-xs text-[color:var(--atech-text-dim)] hover:text-white mb-6">
          <ArrowLeft size={14} /> All projects
        </Link>

        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4 mb-8">
          <div>
            <div className="label text-[color:var(--atech-copper)] mb-2">
              {project.client?.name} · {project.project_reference}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-white leading-tight tracking-tight">
              {project.project_name}
            </h1>
            <div className="mt-3 text-xs text-[color:var(--atech-text-dim)] flex flex-wrap gap-x-5 gap-y-1">
              <span>{project.project_type}</span>
              {project.address && <span>· {project.address}</span>}
              {project.start_date && <span>· Start {formatDate(project.start_date)}</span>}
              {project.due_date && <span>· Due {formatDate(project.due_date)}</span>}
              <span>· Updated {formatDate(project.updated_at)}</span>
            </div>
          </div>
          <StatusPill status={project.current_status} size="lg" />
        </div>

        {project.is_on_hold && (
          <div className="mb-6">
            <OnHoldBanner reason={project.on_hold_reason} date={project.on_hold_date} />
          </div>
        )}

        <div className="card-surface p-8 mb-10">
          <div className="label mb-6">Project stage</div>
          <StatusTimeline
            status={project.current_status}
            isOnHold={project.is_on_hold}
            previousStatus={project.previous_status_before_hold}
          />
          {project.next_milestone && (
            <div className="mt-8 text-sm text-[color:var(--atech-text-dim)]">
              <span className="label mr-3">Next milestone</span>
              {project.next_milestone}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 sm:px-10 pb-16">
        {/* Horizontal tab bar */}
        <div className="flex items-center gap-1 border-b border-[color:var(--atech-border)] mb-8 -mx-1" data-testid="client-project-tabs">
          <ProjectTab to={`/portal/projects/${project.id}`} end label="Overview" testId="client-tab-overview" />
          <ProjectTab to={`/portal/projects/${project.id}/documents`} label="Documents" testId="client-tab-documents" />
          {project.client_uploads_enabled && (
            <ProjectTab to={`/portal/projects/${project.id}/uploads`} label="Uploads" testId="client-tab-uploads" />
          )}
          <ProjectTab to={`/portal/projects/${project.id}/team`} label="Team" testId="client-tab-team" />
        </div>
        <Outlet context={{ project, activity, documents, reload: load }} />
      </div>
    </div>
  );
}


function ProjectTab({ to, end, label, testId }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          "px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors whitespace-nowrap",
          isActive
            ? "border-[color:var(--atech-copper)] text-[color:var(--atech-text)] font-medium"
            : "border-transparent text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:border-[color:var(--atech-border-strong)]",
        ].join(" ")
      }
      data-testid={testId}
    >
      {label}
    </NavLink>
  );
}
